<template>
  <div>
    <!-- Profile Header Card -->
    <b-card class="profile-header mb-2">
      <b-row>
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-center"
        >
          <b-avatar
            :src="profileAvatar"
            size="100"
            class="mr-2"
          />
          <div>
            <h2 class="mb-0">
              {{ profileName }}
            </h2>
            <p class="text-muted mb-0">
              {{ profileJobTitle }}
            </p>
            <p class="text-muted mb-0">
              <small>{{ profileEmail }}</small>
            </p>
          </div>
        </b-col>
        <b-col
          cols="12"
          md="8"
        >
          <b-row class="text-center mt-2 mt-md-0">
            <b-col
              cols="6"
              md="3"
            >
              <div class="d-flex flex-column">
                <feather-icon
                  icon="BriefcaseIcon"
                  size="21"
                  class="mb-1"
                />
                <h3 class="mb-0">
                  {{ totalTasks }}
                </h3>
                <small>إجمالي المهام</small>
              </div>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <div class="d-flex flex-column">
                <feather-icon
                  icon="ClockIcon"
                  size="21"
                  class="mb-1 text-warning"
                />
                <h3 class="mb-0 text-warning">
                  {{ inProgressTasks }}
                </h3>
                <small>قيد التنفيذ</small>
              </div>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <div class="d-flex flex-column">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="21"
                  class="mb-1 text-success"
                />
                <h3 class="mb-0 text-success">
                  {{ completedTasks }}
                </h3>
                <small>مكتملة</small>
              </div>
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <div class="d-flex flex-column">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="21"
                  class="mb-1 text-danger"
                />
                <h3 class="mb-0 text-danger">
                  {{ overdueTasks }}
                </h3>
                <small>متأخرة</small>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabs -->
    <b-card no-body>
      <b-tabs
        v-model="activeTab"
        pills
        card
        content-class="mt-3"
      >
        <!-- Profile Info Tab -->
        <b-tab active>
          <template #title>
            <feather-icon
              icon="UserIcon"
              size="16"
              class="mr-50"
            />
            <span>الملف الشخصي</span>
          </template>
          <profile-info />
        </b-tab>

        <!-- Change Password Tab -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="LockIcon"
              size="16"
              class="mr-50"
            />
            <span>كلمة المرور</span>
          </template>
          <change-password />
        </b-tab>

        <!-- My Tasks Tab -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="CheckSquareIcon"
              size="16"
              class="mr-50"
            />
            <span>المهام</span>
            <b-badge
              v-if="tasksSummary.total > 0"
              variant="primary"
              pill
              class="ml-50"
              size="sm"
            >
              {{ tasksSummary.total }}
            </b-badge>
          </template>
          <my-tasks />
        </b-tab>

        <!-- Permissions Tab -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="ShieldIcon"
              size="16"
              class="mr-50"
            />
            <span>الصلاحيات</span>
          </template>
          <permissions-view />
        </b-tab>

        <!-- Notification Settings Tab -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="BellIcon"
              size="16"
              class="mr-50"
            />
            <span>الإشعارات</span>
          </template>
          <notification-settings />
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BTabs,
  BTab,
  BBadge,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import ProfileInfo from './components/ProfileInfo.vue'
import ChangePassword from './components/ChangePassword.vue'
import MyTasks from './components/MyTasks.vue'
import PermissionsView from './components/PermissionsView.vue'
import NotificationSettings from './components/NotificationSettings.vue'

export default {
  name: 'ProfileView',
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BTabs,
    BTab,
    BBadge,
    ProfileInfo,
    ChangePassword,
    MyTasks,
    PermissionsView,
    NotificationSettings,
  },
  data() {
    return {
      activeTab: 0,
    }
  },
  computed: {
    ...mapGetters('profile', {
      profile: 'profile',
      tasksSummary: 'tasksSummary',
      isLoadingProfile: 'isLoadingProfile',
    }),
    profileAvatar() {
      if (this.profile && this.profile.avatar) {
        return this.profile.avatar
      }
      return require('@/assets/images/profile/profile.png')
    },
    profileName() {
      return this.profile ? this.profile.name : 'جاري التحميل...'
    },
    profileJobTitle() {
      return this.profile ? (this.profile.job_title || 'غير متوفر') : 'غير متوفر'
    },
    profileEmail() {
      return this.profile ? this.profile.email : ''
    },
    totalTasks() {
      return this.tasksSummary ? (this.tasksSummary.total || 0) : 0
    },
    inProgressTasks() {
      return this.tasksSummary ? (this.tasksSummary.in_progress || 0) : 0
    },
    completedTasks() {
      return this.tasksSummary ? (this.tasksSummary.completed || 0) : 0
    },
    overdueTasks() {
      return this.tasksSummary ? (this.tasksSummary.overdue || 0) : 0
    },
  },
  created() {
    this.initializeProfile()
  },
  methods: {
    ...mapActions('profile', {
      initialize: 'initialize',
      fetchTasks: 'fetchTasks',
    }),

    async initializeProfile() {
      try {
        // Initialize profile and permissions
        await this.initialize()

        // Fetch tasks summary
        await this.fetchTasks()
      } catch (error) {
        console.error('Failed to initialize profile:', error)
        this.$toast({
          component: this.$root.$refs.notificationToast,
          props: {
            title: 'خطأ',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'فشل تحميل بيانات الملف الشخصي',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-header {
  .b-avatar {
    border: 3px solid #fff;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

::v-deep .nav-pills {
  .nav-item {
    margin-right: 0.5rem;

    .nav-link {
      display: flex;
      align-items: center;
      padding: 0.75rem 1.5rem;
      border-radius: 0.5rem;
      transition: all 0.3s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      &.active {
        background-color: #007bff;
        color: white;
        box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
      }
    }
  }
}

::v-deep .tab-content {
  .tab-pane {
    padding: 1.5rem;
  }
}
</style>
